import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldAlert, Lock, LogOut } from 'lucide-react';
import api from '../utils/api';

/**
 * Tela de TROCA DE SENHA OBRIGATÓRIA.
 * Exibida quando `user.must_change_password === true` — o backend também bloqueia todas as
 * demais rotas (403 code=must_change_password) até a troca ser concluída. Chama o endpoint
 * self-service /api/auth/change-password, que limpa a flag no servidor.
 */
const TrocaSenhaObrigatoriaPage = ({ user, onDone, onLogout }) => {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nova !== confirma) { toast.error('A nova senha e a confirmação não conferem'); return; }
    if (nova.length < 8 || !/[A-Za-z]/.test(nova) || !/\d/.test(nova)) {
      toast.error('A nova senha deve ter no mínimo 8 caracteres, com letras e números'); return;
    }
    if (nova === senhaAtual) { toast.error('A nova senha deve ser diferente da atual'); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { senha_atual: senhaAtual, nova_senha: nova });
      toast.success('Senha alterada com sucesso!');
      // Limpa a flag no estado local e libera o app (o servidor já limpou no banco).
      onDone?.({ ...user, must_change_password: false });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.msg || 'Não foi possível alterar a senha'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-primary">Troque sua senha</h1>
            <p className="text-sm text-slate-500">Solaric — Gestão</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Por segurança, você precisa definir uma nova senha antes de continuar.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="senha-atual" className="block text-sm font-medium text-slate-700 mb-1">Senha atual</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input id="senha-atual" type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                required autoFocus autoComplete="current-password" />
            </div>
          </div>
          <div>
            <label htmlFor="nova-senha" className="block text-sm font-medium text-slate-700 mb-1">Nova senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input id="nova-senha" type="password" value={nova} onChange={(e) => setNova(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                required minLength={8} autoComplete="new-password" />
            </div>
          </div>
          <div>
            <label htmlFor="confirma-senha" className="block text-sm font-medium text-slate-700 mb-1">Confirmar nova senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input id="confirma-senha" type="password" value={confirma} onChange={(e) => setConfirma(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                required minLength={8} autoComplete="new-password" />
            </div>
          </div>
          <p className="text-xxs text-slate-400">Mínimo 8 caracteres, com letras e números. Diferente da senha atual.</p>
          <button type="submit" disabled={loading}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-60 transition-all">
            {loading ? 'Salvando…' : 'Salvar nova senha'}
          </button>
        </form>

        <button onClick={onLogout} className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-slate-700">
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </div>
  );
};

export default TrocaSenhaObrigatoriaPage;
